/**
 * RefQuantityEditModal — Radix Dialog for editing a single reference quantity
 * from the DAG. Provides a number input and slider for value adjustment,
 * plus a mandatory "reason for change" field for provenance auditing.
 */

import { useEffect, useState } from "react";
import { Button, Dialog, Flex, Slider, Text, TextField } from "@radix-ui/themes";
import type { DAGNode } from "../../api/artifacts";
import { useUpsertReferenceQuantity, useRefQuantityHistory, useReferenceImpact } from "../../hooks/useReferenceQuantities";
import type { ReferenceImpactPreview } from "../../api/referenceQuantities";
import { useBranchGuard } from "../../context/BranchGuardContext";
import { useRefAreaPreview } from "../../context/RefAreaPreviewContext";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useQueryClient } from "@tanstack/react-query";
import { ParameterHistoryStrip } from "../panels/ParameterHistoryStrip";
import { useTheme } from "../../context/ThemeContext";

const AREA_PARAMS = new Set(["wing_area", "ref_area", "s_gross", "s_net"]);

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  branch: string;
  quantity: DAGNode;
  geometryId: string | null;
}

function extractParam(node: DAGNode): string {
  return String(node.path).split(".").pop() ?? "?";
}

function sliderRange(current: number): [number, number] {
  if (current === 0) return [0, 100];
  const magnitude = Math.abs(current);
  return [0, magnitude * 2];
}

function formatImpactKinds(impact: ReferenceImpactPreview): string {
  return Object.entries(impact.affected_by_kind ?? {})
    .filter(([, count]) => count > 0)
    .map(([kind, count]) => `${count} ${kind.replace(/_/g, " ")}`)
    .join(" · ");
}

function ImpactPreview({
  impact,
  loading,
}: {
  impact?: ReferenceImpactPreview;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div style={{ border: "1px solid var(--gray-a5)", borderRadius: 8, padding: 10 }}>
        <Text size="2" color="gray">Checking downstream impact...</Text>
      </div>
    );
  }
  if (!impact) return null;

  const affected = impact.affected_nodes;
  const color = affected > 0 ? "var(--orange-11)" : "var(--green-11)";
  const kinds = formatImpactKinds(impact);

  return (
    <div style={{ border: `1px solid ${affected > 0 ? "var(--orange-a6)" : "var(--green-a6)"}`, borderRadius: 8, padding: 10, background: affected > 0 ? "var(--orange-a2)" : "var(--green-a2)" }}>
      <Text size="2" weight="medium" style={{ color }}>
        {affected} downstream {affected === 1 ? "item" : "items"} affected
      </Text>
      {kinds && (
        <Text as="div" size="1" color="gray" mt="1">
          {kinds}
        </Text>
      )}
      {impact.delta_pct != null && (
        <Text as="div" size="1" color="gray" mt="1">
          Δ {impact.delta_pct > 0 ? "+" : ""}{impact.delta_pct}% from current value
        </Text>
      )}
    </div>
  );
}

export function RefQuantityEditModal({
  open,
  onClose,
  projectId,
  branch,
  quantity,
  geometryId,
}: Props) {
  const currentValue = (quantity.value_scalar as number) ?? 0;
  const units = (quantity.units as string) ?? "";
  const param = extractParam(quantity);

  const [value, setValue] = useState(currentValue);
  const [impactValue, setImpactValue] = useState(currentValue);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [min, max] = sliderRange(currentValue);

  const upsert = useUpsertReferenceQuantity(projectId, branch);
  const { guard } = useBranchGuard();
  const { setBranch } = useProvenanceContext();
  const { setPreviewRefArea } = useRefAreaPreview();
  const qc = useQueryClient();
  const { theme } = useTheme();
  const isAreaParam = AREA_PARAMS.has(param);
  const { data: history } = useRefQuantityHistory(quantity.id, projectId, branch);
  const [selectedHistoryValues] = useState<Set<number>>(() => new Set());
  const valueChanged = Math.abs(impactValue - currentValue) > 1e-12;
  const { data: impact, isFetching: impactLoading } = useReferenceImpact(
    projectId,
    branch,
    param,
    impactValue,
    open && valueChanged,
    String(quantity.path),
  );

  function updateValue(v: number) {
    setValue(v);
    if (isAreaParam) setPreviewRefArea(v);
  }

  function clearPreview() {
    if (isAreaParam) setPreviewRefArea(null);
  }

  useEffect(() => {
    if (open) {
      setValue(currentValue);
      setImpactValue(currentValue);
      setReason("");
      setError(null);
      if (isAreaParam) setPreviewRefArea(currentValue);
    } else {
      clearPreview();
    }
  }, [open, currentValue]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setImpactValue(value), 250);
    return () => window.clearTimeout(timer);
  }, [open, value]);

  function handleSave() {
    if (!reason.trim()) {
      setError("A reason for change is required");
      return;
    }

    const payload = {
      project_id: projectId,
      parameter: param,
      value,
      units: units || undefined,
      geometry_id: geometryId ?? undefined,
      change_reason: reason.trim(),
    };

    clearPreview();
    onClose();

    guard(() => {
      upsert.mutate(payload, {
        onSuccess: (data) => {
          if (data.forced_branch === true && typeof data.branch === "string") {
            setBranch(data.branch);
          }
          qc.invalidateQueries({ queryKey: ["provenance"] });
          qc.invalidateQueries({ queryKey: ["ref-quantity-history"] });
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : String(err));
        },
      });
    }, { hint: param });
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) { clearPreview(); onClose(); } }}>
      <Dialog.Content maxWidth="440px">
        <Dialog.Title>
          Edit {param}
          {units ? ` (${units})` : ""}
        </Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Current value: {currentValue}
          {units ? ` ${units}` : ""}
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          <label>
            <Text size="2" weight="medium" mb="1" asChild>
              <span>Value</span>
            </Text>
            <TextField.Root
              type="number"
              value={String(value)}
              onChange={(e) => updateValue(Number(e.target.value))}
            />
          </label>

          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">Slider</Text>
            <Slider
              min={min}
              max={max}
              step={max > 100 ? 1 : 0.01}
              value={[value]}
              onValueChange={([v]) => updateValue(v ?? value)}
            />
            <Flex justify="between">
              <Text size="1" color="gray">{min}</Text>
              <Text size="1" color="gray">{max}</Text>
            </Flex>
          </Flex>

          {history && history.length >= 2 && (
            <ParameterHistoryStrip
              history={history}
              currentValue={currentValue}
              selectedValues={selectedHistoryValues}
              onToggleValue={(v) => updateValue(v)}
              units={units || null}
              isLight={theme === "light"}
            />
          )}

          <ImpactPreview impact={impact} loading={impactLoading && valueChanged} />

          <label>
            <Text size="2" weight="medium" mb="1" asChild>
              <span>Reason for change</span>
            </Text>
            <TextField.Root
              placeholder="e.g. Updated from wind tunnel data"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
            />
          </label>

          {error && (
            <Text size="1" color="red">{error}</Text>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? "Saving..." : "Save"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
